import React from 'react'
import { FaTrash, FaPlus } from 'react-icons/fa'
import { Alert, Box, Button, Checkbox, Heading, Input, Select, Stack, Text } from '@/components/lib'
import { newTableColumnId } from '@/utils/utils'

// The formats a cell can take. Deliberately the same keys as the scalar data
// types they are named after, so a cell is coerced by the same code path on the
// API rather than through a parallel vocabulary of its own.
export const TABLE_COLUMN_TYPES = [
  { key: 'text', label: 'Text' },
  { key: 'integer', label: 'Integer' },
  { key: 'float', label: 'Decimal' },
  { key: 'date', label: 'Date' }
]

export const MAX_TABLE_COLUMNS = 20
export const MAX_TABLE_ROWS = 50

function moveItem (items, from, to) {
  if (to < 0 || to >= items.length) return items
  const next = [...items]
  const [item] = next.splice(from, 1)
  next.splice(to, 0, item)
  return next
}

/**
 * The column editor for a table data type.
 *
 * Every column carries an opaque `id` that this editor round-trips untouched —
 * that is what makes renaming and reordering safe once cells have been
 * collected, since each cell is stored against its column's id rather than its
 * name.
 */
export default function TableColumnsEditor ({ columns, setColumns, maximumRows, setMaximumRows, isEditing }) {
  const updateColumn = (index, changes) =>
    setColumns(columns.map((c, i) => i === index ? { ...c, ...changes } : c))

  return (
    <Stack gap={5}>
      <Stack gap={3}>
        <Heading as='h4' size='sm'>The columns</Heading>
        <Text fontSize='sm' color='fg.muted'>
          Each column has a name and a format. Mark a column optional if a row may be entered without it.
        </Text>
        {isEditing &&
          <Alert status='warning' variant='left-accent' title='Changing columns once responses exist'>
            You can safely rename and reorder columns and retain data submitted so far. If data has been captured on this table, we recommend against removing columns or changing column formats, as this may cause data to be lost.
          </Alert>}
        <Stack gap={2}>
          {columns.map((column, index) =>
            <Stack direction={{ base: 'column', md: 'row' }} gap={2} key={column.id} alignItems={{ md: 'center' }}>
              <Input
                size='xs'
                placeholder='Column name, e.g. Dose'
                value={column.name ?? ''}
                onChange={e => updateColumn(index, { name: e.target.value })}
              />
              <Box w={{ base: 'full', md: '200px' }}>
                <Select
                  size='xs'
                  value={column.type ?? 'text'}
                  onChange={e => updateColumn(index, { type: e.target.value })}
                >
                  {TABLE_COLUMN_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                </Select>
              </Box>
              <Checkbox
                isChecked={!!column.isOptional}
                onChange={e => updateColumn(index, { isOptional: e.checked })}
              >Optional
              </Checkbox>
              <Button
                variant='outline' size='sm' aria-label='Move column up' disabled={index === 0}
                onClick={() => setColumns(moveItem(columns, index, index - 1))}
              >↑
              </Button>
              <Button
                variant='outline' size='sm' aria-label='Move column down' disabled={index === columns.length - 1}
                onClick={() => setColumns(moveItem(columns, index, index + 1))}
              >↓
              </Button>
              <Button
                variant='ghost' size='sm' aria-label='Remove column'
                onClick={() => setColumns(columns.filter((c, i) => i !== index))}
              ><FaTrash />
              </Button>
            </Stack>
          )}
        </Stack>
        {!columns.length && <Text fontSize='sm' color='fg.muted'>No columns yet — add at least one.</Text>}
        <Box>
          <Button
            variant='outline' size='sm'
            disabled={columns.length >= MAX_TABLE_COLUMNS}
            onClick={() => setColumns([...columns, { id: newTableColumnId(), name: '', type: 'text', isOptional: false }])}
          ><FaPlus /> Add a column
          </Button>
        </Box>
      </Stack>

      <Stack gap={2}>
        <Heading as='h4' size='sm'>Maximum rows</Heading>
        <Text fontSize='sm' color='fg.muted'>
          The most rows a respondent may fill in, between 1 and {MAX_TABLE_ROWS}.
        </Text>
        <Box maxW='150px'>
          <Input
            type='number' min={1} max={MAX_TABLE_ROWS}
            value={maximumRows ?? ''}
            onChange={e => setMaximumRows(e.target.value === '' ? '' : parseInt(e.target.value))}
          />
        </Box>
      </Stack>
    </Stack>
  )
}
